import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChecklistItemResponse,
  CreateChecklistItemInput,
  CreateProjectTaskInput,
  ItemResponse,
  ListResponse,
  MyProjectTaskResponse,
  ProjectTaskResponse,
  ProjectTaskSummary,
  ReorderChecklistItemsInput,
  ReorderProjectTasksInput,
  UpdateChecklistItemInput,
  UpdateProjectTaskInput,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

const tasksKey = (projectId: string) => ['projects', 'detail', projectId, 'tasks'] as const;
const checklistKey = (projectId: string) => ['projects', 'detail', projectId, 'checklist'] as const;

// ── Aufgaben ────────────────────────────────────────────────────────────────
export function useProjectTasks(projectId: string) {
  return useQuery({
    queryKey: tasksKey(projectId),
    queryFn: () => apiRequest<ListResponse<ProjectTaskResponse>>(`/v1/projects/${projectId}/tasks`),
    enabled: Boolean(projectId),
  });
}

function useInvalidateTasks(projectId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: tasksKey(projectId) });
    void queryClient.invalidateQueries({ queryKey: ['project-tasks', 'summary'] });
  };
}

export function useCreateProjectTask(projectId: string) {
  const invalidate = useInvalidateTasks(projectId);
  return useMutation({
    mutationFn: (input: CreateProjectTaskInput) =>
      apiRequest<ItemResponse<ProjectTaskResponse>>(`/v1/projects/${projectId}/tasks`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateProjectTask(projectId: string) {
  const invalidate = useInvalidateTasks(projectId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProjectTaskInput }) =>
      apiRequest<ItemResponse<ProjectTaskResponse>>(`/v1/projects/${projectId}/tasks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteProjectTask(projectId: string) {
  const invalidate = useInvalidateTasks(projectId);
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/projects/${projectId}/tasks/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useDeleteAllProjectTasks(projectId: string) {
  const invalidate = useInvalidateTasks(projectId);
  return useMutation({
    mutationFn: () =>
      apiRequest<{ data: { deleted: number } }>(`/v1/projects/${projectId}/tasks`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useReorderProjectTasks(projectId: string) {
  const invalidate = useInvalidateTasks(projectId);
  return useMutation({
    mutationFn: (input: ReorderProjectTasksInput) =>
      apiRequest<ListResponse<ProjectTaskResponse>>(`/v1/projects/${projectId}/tasks/reorder`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useInitProjectTasks(projectId: string) {
  const invalidate = useInvalidateTasks(projectId);
  return useMutation({
    mutationFn: () =>
      apiRequest<ListResponse<ProjectTaskResponse>>(`/v1/projects/${projectId}/tasks/init-from-template`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: invalidate,
  });
}

/** Dashboard-Kennzahlen: offene Aufgaben, fällig ≤ 7 Tage, überfällig. */
export function useProjectTaskSummary() {
  return useQuery({
    queryKey: ['project-tasks', 'summary'],
    queryFn: () => apiRequest<ItemResponse<ProjectTaskSummary>>('/v1/project-tasks/summary'),
  });
}

/** Meine offenen Projektaufgaben über alle Projekte (Dashboard-Widget, ADR 0062). */
export function useMyProjectTasks() {
  return useQuery({
    queryKey: ['project-tasks', 'mine'],
    queryFn: () => apiRequest<ListResponse<MyProjectTaskResponse>>('/v1/project-tasks/mine'),
  });
}

// ── Checkliste ──────────────────────────────────────────────────────────────
export function useProjectChecklist(projectId: string) {
  return useQuery({
    queryKey: checklistKey(projectId),
    queryFn: () => apiRequest<ListResponse<ChecklistItemResponse>>(`/v1/projects/${projectId}/checklist`),
    enabled: Boolean(projectId),
  });
}

function useInvalidateChecklist(projectId: string) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: checklistKey(projectId) });
}

export function useCreateChecklistItem(projectId: string) {
  const invalidate = useInvalidateChecklist(projectId);
  return useMutation({
    mutationFn: (input: CreateChecklistItemInput) =>
      apiRequest<ItemResponse<ChecklistItemResponse>>(`/v1/projects/${projectId}/checklist`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateChecklistItem(projectId: string) {
  const invalidate = useInvalidateChecklist(projectId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateChecklistItemInput }) =>
      apiRequest<ItemResponse<ChecklistItemResponse>>(`/v1/projects/${projectId}/checklist/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteChecklistItem(projectId: string) {
  const invalidate = useInvalidateChecklist(projectId);
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/projects/${projectId}/checklist/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useReorderChecklistItems(projectId: string) {
  const invalidate = useInvalidateChecklist(projectId);
  return useMutation({
    mutationFn: (input: ReorderChecklistItemsInput) =>
      apiRequest<ListResponse<ChecklistItemResponse>>(`/v1/projects/${projectId}/checklist/reorder`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useInitProjectChecklist(projectId: string) {
  const invalidate = useInvalidateChecklist(projectId);
  return useMutation({
    mutationFn: () =>
      apiRequest<ListResponse<ChecklistItemResponse>>(`/v1/projects/${projectId}/checklist/init-from-template`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: invalidate,
  });
}
